import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Calendar from '../components/Calendar';
import { Clock, Users, Calendar as CalendarIcon, TrendingUp, AlertCircle, X, MapPin, Home, PartyPopper, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';
import GroupBadge from '../components/GroupBadge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [currentEntry, setCurrentEntry] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [showLeaveBanner, setShowLeaveBanner] = useState(true);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showActiveEmployeesDialog, setShowActiveEmployeesDialog] = useState(false);

  useEffect(() => {
    // Detect if device is mobile using multiple methods
    const checkMobile = () => {
      // Method 1: User-Agent check
      const userAgent = navigator.userAgent || '';
      const userAgentMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

      // Method 2: Touch support check
      const hasTouchScreen = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
      );

      // Method 3: Screen width check (mobile devices typically < 768px)
      const isSmallScreen = window.innerWidth < 768;

      // Method 4: Check for mobile-specific features
      const hasOrientationAPI = 'orientation' in window || 'onorientationchange' in window;

      // Combine checks: if any strong mobile indicator is present
      const mobile = userAgentMobile || (hasTouchScreen && isSmallScreen) || (hasTouchScreen && hasOrientationAPI);

      setIsMobile(mobile);
    };

    checkMobile();
    fetchDashboardData();
    if (user.role === 'admin') {
      fetchGroups();
      fetchEmployees();
    }
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await authFetch(`${API}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/team/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const requests = [
        authFetch(`${API}/time/current`),
        authFetch(`${API}/reports/summary`),
        authFetch(`${API}/time/history?limit=5`),
        authFetch(`${API}/holidays/upcoming`),
        authFetch(`${API}/holidays`),
        authFetch(`${API}/leave/balance`)
      ];

      if (user.role === 'admin') {
        requests.push(authFetch(`${API}/leave/all`));
      } else {
        requests.push(authFetch(`${API}/leave/my-requests`));
      }

      const responses = await Promise.all(requests);
      const [currentRes, summaryRes, historyRes, upcomingHolidaysRes, allHolidaysRes, balanceRes, leavesRes] = responses;

      const current = await currentRes.json();
      const summaryData = await summaryRes.json();
      const history = await historyRes.json();
      const upcomingHols = await upcomingHolidaysRes.json();
      const allHols = await allHolidaysRes.json();
      const balance = await balanceRes.json();

      setCurrentEntry(current);
      setSummary(summaryData);
      setRecentActivity(history);
      setUpcomingHolidays(upcomingHols);
      setAllHolidays(allHols);
      setLeaveBalance(balance);

      if (leavesRes) {
        const leaves = await leavesRes.json();
        setAllLeaves(leaves);
        if (user.role === 'admin') {
          const pending = leaves.filter(l => l.status === 'pending');
          setPendingLeaves(pending);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLocationDialog = () => {
    setShowLocationDialog(true);
    setSelectedLocation('');
  };

  const handleClockIn = async (location) => {
    try {
      const response = await authFetch(`${API}/time/clock-in`, {
        method: 'POST',
        body: JSON.stringify({ location })
      });

      if (response.ok) {
        const entry = await response.json();
        setCurrentEntry(entry);
        setShowLocationDialog(false);
        const locationLabels = {
          'onsite': 'Onsite',
          'wfh': 'Work from Home',
          'event': 'Event',
          'field': 'Field'
        };
        const locationLabel = locationLabels[location] || location;
        toast.success(`Clocked in successfully (${locationLabel})!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-out`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        toast.success(`Clocked out! Duration: ${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m`);
        setCurrentEntry(null);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Failed to clock out');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave approved');
        fetchDashboardData();
      } else {
        toast.error('Failed to approve leave');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/reject`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave rejected');
        fetchDashboardData();
      } else {
        toast.error('Failed to reject leave');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const formatDuration = (clockIn) => {
    const start = new Date(clockIn);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-2">
            Welcome back, {user.name}!
          </h1>
          <p className="text-foreground-muted">Here's your activity overview</p>
        </div>

        {user.role === 'admin' && pendingLeaves.length > 0 && showLeaveBanner && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="leave-notification-banner">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">
                    {pendingLeaves.length} Pending Leave Request{pendingLeaves.length > 1 ? 's' : ''}
                  </h3>
                  <div className="space-y-2">
                    {pendingLeaves.slice(0, 3).map((leave) => (
                      <div key={leave.leave_id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-foreground-DEFAULT">
                            {leave.user_name}
                            {leave.is_half_day && (
                              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                Half Day ({leave.half_day_period || 'Morning'})
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-foreground-muted">
                            {leave.start_date} to {leave.end_date} ({leave.leave_type}) • {leave.leave_days || '?'} day(s)
                          </p>
                          {leave.created_at && (
                            <p className="text-xs text-foreground-muted/80 mt-0.5">
                              Submitted: {new Date(leave.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(leave.leave_id)}
                            className="px-3 py-1 bg-accent text-white rounded-full text-xs font-medium hover:bg-accent/90"
                            data-testid={`quick-approve-${leave.leave_id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(leave.leave_id)}
                            className="px-3 py-1 bg-destructive text-white rounded-full text-xs font-medium hover:bg-destructive/90"
                            data-testid={`quick-reject-${leave.leave_id}`}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {pendingLeaves.length > 3 && (
                    <button
                      onClick={() => navigate('/leave')}
                      className="text-sm text-orange-700 hover:text-orange-800 font-medium mt-2"
                    >
                      View all {pendingLeaves.length} requests →
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowLeaveBanner(false)}
                className="text-orange-600 hover:text-orange-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {leaveBalance && user.role !== 'admin' && (
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-slate-100 shadow-sm rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-heading font-semibold text-foreground-DEFAULT mb-4">
              Leave Balance {leaveBalance.year}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4">
                <p className="text-sm text-foreground-muted mb-1">Sick Leave</p>
                <p className={`text-2xl font-bold ${leaveBalance.sick_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'}`}>
                  {leaveBalance.sick_leave} days
                </p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-sm text-foreground-muted mb-1">Vacation Leave</p>
                <p className={`text-2xl font-bold ${leaveBalance.vacation_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'}`}>
                  {leaveBalance.vacation_leave} days
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {user.role !== 'admin' && (
            <div className="md:col-span-5">
              <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-8 flex flex-col items-center justify-center" data-testid="timer-widget">
                {isMobile ? (
                <div className="text-center">
                  <div className="bg-orange-100 rounded-full w-32 h-32 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-16 h-16 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-DEFAULT mb-2">Desktop Only</h3>
                  <p className="text-sm text-foreground-muted max-w-sm">
                    Time tracking is only available on desktop devices. Please use a computer to clock in and out.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="text-sm font-medium text-foreground-muted text-center mb-2">
                      {currentEntry ? 'Currently Working' : 'Not Clocked In'}
                    </p>
                    {currentEntry && (
                      <p className="text-4xl font-heading font-bold text-foreground-DEFAULT text-center">
                        {formatDuration(currentEntry.clock_in)}
                      </p>
                    )}
                  </div>

                  {currentEntry ? (
                    <button
                      data-testid="clock-out-btn"
                      onClick={handleClockOut}
                      className="bg-destructive text-white rounded-full w-32 h-32 flex flex-col items-center justify-center text-xl font-bold shadow-xl shadow-destructive/20 hover:scale-105 transition-transform"
                    >
                      <Clock className="w-8 h-8 mb-2" />
                      Clock Out
                    </button>
                  ) : (
                    <button
                      data-testid="clock-in-btn"
                      onClick={openLocationDialog}
                      className="bg-accent text-white rounded-full w-32 h-32 flex flex-col items-center justify-center text-xl font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform"
                    >
                      <Clock className="w-8 h-8 mb-2" />
                      Clock In
                    </button>
                  )}
                </>
              )}
              </div>
            </div>
          )}

          <div className={user.role === 'admin' ? 'md:col-span-12' : 'md:col-span-7'}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user.role === 'admin' ? (
                <>
                  {/* Clock In Button - Top Left */}
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center" data-testid="admin-clock-widget">
                    {isMobile ? (
                      <div className="text-center">
                        <div className="bg-orange-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-3">
                          <AlertCircle className="w-12 h-12 text-orange-600" />
                        </div>
                        <h3 className="text-sm font-semibold text-foreground-DEFAULT mb-1">Desktop Only</h3>
                        <p className="text-xs text-foreground-muted">
                          Time tracking requires desktop
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 text-center">
                          <p className="text-xs font-medium text-foreground-muted mb-2">
                            {currentEntry ? 'Currently Working' : 'Not Clocked In'}
                          </p>
                          {currentEntry && (
                            <p className="text-2xl font-heading font-bold text-foreground-DEFAULT">
                              {formatDuration(currentEntry.clock_in)}
                            </p>
                          )}
                        </div>

                        {currentEntry ? (
                          <button
                            data-testid="clock-out-btn"
                            onClick={handleClockOut}
                            className="bg-destructive text-white rounded-full w-28 h-28 flex flex-col items-center justify-center text-lg font-bold shadow-xl shadow-destructive/20 hover:scale-105 transition-transform"
                          >
                            <Clock className="w-6 h-6 mb-1" />
                            Clock Out
                          </button>
                        ) : (
                          <button
                            data-testid="clock-in-btn"
                            onClick={openLocationDialog}
                            className="bg-accent text-white rounded-full w-28 h-28 flex flex-col items-center justify-center text-lg font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform"
                          >
                            <Clock className="w-6 h-6 mb-1" />
                            Clock In
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Active Now - Top Right */}
                  <div
                    className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                    data-testid="stat-card-active"
                    onClick={() => summary?.active_employees?.length > 0 && setShowActiveEmployeesDialog(true)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-accent/10 rounded-xl p-3">
                          <Clock className="w-6 h-6 text-accent" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground-DEFAULT">Active Now</h3>
                          <p className="text-sm text-foreground-muted">Currently working</p>
                        </div>
                      </div>
                      <p className="text-4xl font-heading font-bold text-foreground-DEFAULT">
                        {summary?.active_today || 0}
                      </p>
                    </div>
                    {summary?.active_employees && summary.active_employees.length > 0 ? (
                      <div className="space-y-2">
                        {summary.active_employees.slice(0, 3).map((emp) => {
                          const locationIcons = {
                            'onsite': '🗺️',
                            'wfh': '🏠',
                            'event': '🎉',
                            'field': '💼'
                          };
                          return (
                            <div key={emp.user_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground-DEFAULT truncate">{emp.name}</p>
                                <p className="text-xs text-foreground-muted truncate">{emp.email}</p>
                              </div>
                              <span className="text-sm ml-2">
                                {locationIcons[emp.location] || '💼'}
                              </span>
                            </div>
                          );
                        })}
                        {summary.active_employees.length > 3 && (
                          <button className="w-full text-center text-sm text-accent font-medium hover:text-accent/80 transition-colors pt-2">
                            +{summary.active_employees.length - 3} more - Click to view all →
                          </button>
                        )}
                        {summary.active_employees.length <= 3 && (
                          <p className="text-center text-xs text-foreground-muted pt-1">
                            Click card to view details
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-foreground-muted">No employees currently working</p>
                      </div>
                    )}
                  </div>

                  {/* Total Employees - Bottom Left */}
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-employees">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-primary/10 rounded-xl p-3">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Employees</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_employees || 0}
                    </p>
                  </div>

                  {/* Pending Leaves - Bottom Right */}
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-leaves">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-orange-100 rounded-xl p-3">
                        <CalendarIcon className="w-6 h-6 text-orange-600" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Pending Leaves</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.pending_leaves || 0}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-hours">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-primary/10 rounded-xl p-3">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Hours</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_hours || 0}h
                    </p>
                  </div>

                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-entries">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-accent/10 rounded-xl p-3">
                        <CalendarIcon className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Entries</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_entries || 0}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {user.role === 'admin' && groups.length > 0 && (
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT">Groups Overview</h2>
              <button
                onClick={() => navigate('/team')}
                className="text-sm text-primary hover:text-primary-hover font-medium"
              >
                Manage Groups →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.map((group) => {
                const groupEmployees = employees.filter(emp => emp.group_id === group.group_id);
                const activeCount = groupEmployees.length;

                return (
                  <div
                    key={group.group_id}
                    className="bg-slate-50 rounded-xl p-4 border-l-4"
                    style={{ borderLeftColor: group.color }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <GroupBadge group={group} showIcon={false} />
                    </div>
                    <h3 className="font-semibold text-foreground-DEFAULT mb-1">{group.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-foreground-muted">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{activeCount} {activeCount === 1 ? 'member' : 'members'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {employees.filter(emp => !emp.group_id).length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 border-l-4 border-slate-300">
                  <div className="mb-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-700">
                      Unassigned
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground-DEFAULT mb-1">No Group</h3>
                  <div className="flex items-center gap-4 text-sm text-foreground-muted">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{employees.filter(emp => !emp.group_id).length} {employees.filter(emp => !emp.group_id).length === 1 ? 'member' : 'members'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="recent-activity">
            <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">Recent Activity</h2>
            {recentActivity.length === 0 ? (
              <p className="text-foreground-muted text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((entry) => (
                  <div
                    key={entry.entry_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                    data-testid={`activity-entry-${entry.entry_id}`}
                  >
                    <div>
                      <p className="font-medium text-foreground-DEFAULT">
                        {new Date(entry.clock_in).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-sm text-foreground-muted">
                        {new Date(entry.clock_in).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {entry.clock_out && (
                          <>
                            {' - '}
                            {new Date(entry.clock_out).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground-DEFAULT">
                        {entry.duration ? `${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m` : 'In Progress'}
                      </p>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          entry.status === 'active'
                            ? 'bg-accent/10 text-accent'
                            : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        {entry.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="upcoming-holidays">
            <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">Upcoming Holidays</h2>
            {upcomingHolidays.length === 0 ? (
              <p className="text-foreground-muted text-center py-8">No upcoming holidays</p>
            ) : (
              <div className="space-y-3">
                {upcomingHolidays.map((holiday) => (
                  <div
                    key={holiday.holiday_id}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl"
                    data-testid={`holiday-${holiday.holiday_id}`}
                  >
                    <div>
                      <p className="font-medium text-foreground-DEFAULT">{holiday.name}</p>
                      <p className="text-sm text-foreground-muted">
                        {new Date(holiday.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                      {holiday.description && (
                        <p className="text-xs text-foreground-muted mt-1">{holiday.description}</p>
                      )}
                    </div>
                    <CalendarIcon className="w-8 h-8 text-primary/60" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Calendar
            holidays={allHolidays}
            leaves={allLeaves}
            onDateClick={(_dateKey, events) => {
              if (events.holidays.length > 0 || events.leaves.length > 0) {
                let message = [];
                if (events.holidays.length > 0) {
                  message.push(`Holidays: ${events.holidays.map(h => h.name).join(', ')}`);
                }
                if (events.leaves.length > 0) {
                  message.push(`Leaves: ${events.leaves.map(l => `${l.user_name} (${l.status})`).join(', ')}`);
                }
                toast.info(message.join(' | '));
              }
            }}
          />
        </div>

        {/* Active Employees Dialog */}
        <Dialog open={showActiveEmployeesDialog} onOpenChange={setShowActiveEmployeesDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Active Employees ({summary?.active_employees?.length || 0})</DialogTitle>
              <DialogDescription>
                Employees currently clocked in and working
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {summary?.active_employees && summary.active_employees.length > 0 ? (
                summary.active_employees.map((emp) => {
                  const clockInDate = new Date(emp.clock_in);
                  const now = new Date();
                  const diffMinutes = Math.floor((now - clockInDate) / 1000 / 60);
                  const hours = Math.floor(diffMinutes / 60);
                  const minutes = diffMinutes % 60;
                  const duration = `${hours}h ${minutes}m`;

                  const locationInfo = {
                    'onsite': { icon: <MapPin className="w-4 h-4" />, label: 'Onsite', color: 'bg-blue-50 text-blue-700' },
                    'wfh': { icon: <Home className="w-4 h-4" />, label: 'Work from Home', color: 'bg-green-50 text-green-700' },
                    'event': { icon: <PartyPopper className="w-4 h-4" />, label: 'Event', color: 'bg-purple-50 text-purple-700' },
                    'field': { icon: <Briefcase className="w-4 h-4" />, label: 'Field', color: 'bg-orange-50 text-orange-700' }
                  };

                  const location = locationInfo[emp.location] || { icon: <Clock className="w-4 h-4" />, label: 'Working', color: 'bg-gray-50 text-gray-700' };

                  return (
                    <div
                      key={emp.user_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-foreground-DEFAULT">{emp.name}</p>
                        <p className="text-sm text-foreground-muted">{emp.email}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground-DEFAULT">{duration}</p>
                          <p className="text-xs text-foreground-muted">
                            Since {clockInDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${location.color}`}>
                          {location.icon}
                          <span className="text-sm font-medium">{location.label}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                  <p className="text-foreground-muted">No employees currently active</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowActiveEmployeesDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Location Selection Dialog */}
        <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Work Location</DialogTitle>
              <DialogDescription>
                Where are you working today?
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <button
                onClick={() => setSelectedLocation('onsite')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'onsite'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <MapPin className={`w-8 h-8 ${selectedLocation === 'onsite' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Onsite</span>
              </button>
              <button
                onClick={() => setSelectedLocation('wfh')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'wfh'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <Home className={`w-8 h-8 ${selectedLocation === 'wfh' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Work from Home</span>
              </button>
              <button
                onClick={() => setSelectedLocation('event')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'event'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <PartyPopper className={`w-8 h-8 ${selectedLocation === 'event' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Event</span>
              </button>
              <button
                onClick={() => setSelectedLocation('field')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'field'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <Briefcase className={`w-8 h-8 ${selectedLocation === 'field' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Field</span>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleClockIn(selectedLocation)}
                disabled={!selectedLocation}
              >
                Clock In
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Dashboard;